'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { logger } from '@/lib/client-logger';
import { getManifest, listManifests } from './manifest';
import { DEFAULT_STT_PROVIDER_ID, DEFAULT_TTS_PROVIDER_ID } from './registry.client';
import type { ProviderId, ProviderManifest, SpeechKind } from './types';

export const SPEECH_SETTINGS_STORAGE_KEY = 'tadm.speech.settings.v1';

export interface SpeechSettings {
  ttsProviderId: ProviderId;
  sttProviderId: ProviderId;
}

export interface SpeechSettingsContextValue extends SpeechSettings {
  /**
   * false during SSR and the first client render. Gate any text that reads from
   * storage on this, or the server and client markup will disagree.
   */
  hydrated: boolean;
  ttsManifest?: ProviderManifest;
  sttManifest?: ProviderManifest;
  ttsOptions: ProviderManifest[];
  sttOptions: ProviderManifest[];
  setTtsProvider(id: ProviderId): void;
  setSttProvider(id: ProviderId): void;
  resetToDefaults(): void;
}

const DEFAULTS: SpeechSettings = {
  ttsProviderId: DEFAULT_TTS_PROVIDER_ID,
  sttProviderId: DEFAULT_STT_PROVIDER_ID,
};

const SpeechSettingsContext = createContext<SpeechSettingsContextValue | null>(null);

/** A stored id is only honoured if it still exists and still declares the capability. */
function sanitiseId(id: unknown, kind: SpeechKind, fallback: ProviderId): ProviderId {
  if (typeof id !== 'string' || !id) return fallback;
  const manifest = getManifest(id);
  if (!manifest) {
    logger.warn('speech settings: unknown provider id, falling back', { id, kind }, 'SpeechSettings');
    return fallback;
  }
  if (!manifest.capabilities[kind]) {
    logger.warn('speech settings: provider lacks capability, falling back', { id, kind }, 'SpeechSettings');
    return fallback;
  }
  return id;
}

function readStored(): SpeechSettings | null {
  try {
    const raw = window.localStorage.getItem(SPEECH_SETTINGS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      ttsProviderId: sanitiseId(parsed?.ttsProviderId, 'tts', DEFAULTS.ttsProviderId),
      sttProviderId: sanitiseId(parsed?.sttProviderId, 'stt', DEFAULTS.sttProviderId),
    };
  } catch {
    return null;
  }
}

/** ?tts=…&stt=… — try a vendor on a kiosk without touching the settings screen. */
function readQueryOverride(): Partial<SpeechSettings> {
  const out: Partial<SpeechSettings> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    const tts = params.get('tts');
    const stt = params.get('stt');
    if (tts && getManifest(tts)?.capabilities.tts) out.ttsProviderId = tts;
    if (stt && getManifest(stt)?.capabilities.stt) out.sttProviderId = stt;
  } catch {}
  return out;
}

export function SpeechSettingsProvider({ children }: { children: React.ReactNode }) {
  // Initialised from the module defaults only — no storage read here, so the
  // server HTML and the first client render are byte-identical.
  const [settings, setSettings] = useState<SpeechSettings>(DEFAULTS);
  const [hydrated, setHydrated] = useState(false);
  /** query overrides are session-only and must never be written back to storage */
  const ephemeralRef = useRef(false);

  useEffect(() => {
    const stored = readStored();
    const override = readQueryOverride();
    const next = { ...DEFAULTS, ...(stored ?? {}), ...override };
    ephemeralRef.current = Object.keys(override).length > 0;

    setSettings(next);
    setHydrated(true);

    // Rewrite the sanitised value so a removed provider id self-heals instead of
    // warning on every load. Skipped when a query override is in play.
    if (stored && !ephemeralRef.current) {
      try {
        window.localStorage.setItem(SPEECH_SETTINGS_STORAGE_KEY, JSON.stringify(next));
      } catch {}
    }
  }, []);

  const persist = useCallback((next: SpeechSettings) => {
    setSettings(next);
    ephemeralRef.current = false;
    try {
      window.localStorage.setItem(SPEECH_SETTINGS_STORAGE_KEY, JSON.stringify(next));
    } catch {}
  }, []);

  const setTtsProvider = useCallback((id: ProviderId) => {
    setSettings((prev) => {
      const next = { ...prev, ttsProviderId: sanitiseId(id, 'tts', prev.ttsProviderId) };
      try { window.localStorage.setItem(SPEECH_SETTINGS_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
    ephemeralRef.current = false;
  }, []);

  const setSttProvider = useCallback((id: ProviderId) => {
    setSettings((prev) => {
      const next = { ...prev, sttProviderId: sanitiseId(id, 'stt', prev.sttProviderId) };
      try { window.localStorage.setItem(SPEECH_SETTINGS_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
    ephemeralRef.current = false;
  }, []);

  const resetToDefaults = useCallback(() => persist(DEFAULTS), [persist]);

  const value = useMemo<SpeechSettingsContextValue>(() => ({
    ...settings,
    hydrated,
    ttsManifest: getManifest(settings.ttsProviderId),
    sttManifest: getManifest(settings.sttProviderId),
    ttsOptions: listManifests('tts'),
    sttOptions: listManifests('stt'),
    setTtsProvider,
    setSttProvider,
    resetToDefaults,
  }), [settings, hydrated, setTtsProvider, setSttProvider, resetToDefaults]);

  return <SpeechSettingsContext.Provider value={value}>{children}</SpeechSettingsContext.Provider>;
}

/**
 * Falls back to the defaults when no provider is mounted, so a component can be
 * rendered in isolation (or in the admin dashboard) without crashing.
 */
export function useSpeechSettings(): SpeechSettingsContextValue {
  const ctx = useContext(SpeechSettingsContext);
  if (ctx) return ctx;
  return {
    ...DEFAULTS,
    hydrated: false,
    ttsManifest: getManifest(DEFAULTS.ttsProviderId),
    sttManifest: getManifest(DEFAULTS.sttProviderId),
    ttsOptions: listManifests('tts'),
    sttOptions: listManifests('stt'),
    setTtsProvider: () => {},
    setSttProvider: () => {},
    resetToDefaults: () => {},
  };
}
